import { isPossiblePhoneNumber, type CountryCode } from 'libphonenumber-js';

import { type Country } from '~/constants/countries';
import { FullStoryEvent, fullStoryTracking } from '~/utils/fullstory/tracking';

const sanitizeText = (text) =>
  // eslint-disable-next-line no-control-regex
  text.replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2066-\u2069]/g, '');

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const insuranceValuesRegex = /^\d{1,5}(,\d{3})*(\.\d{0,2})?$/;

export const exists = (value) => !!value;

export const hasLength = (minLength, maxLength, value) => {
  if (minLength && value.length < minLength) return false;

  if (maxLength && value.length > maxLength) return false;

  return true;
};

export const isDecimal = (value) => {
  const decimalRegex = /^\d*\.?\d*$/;
  return decimalRegex.test(value);
};

export const isNonZero = (value: number | string) => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  return numericValue !== 0;
};

export const isPositiveNumber = (value: number | string) => {
  const numericValue = typeof value === 'string' ? parseFloat(value.trim()) : value;
  return Number.isFinite(numericValue) && numericValue > 0;
};

export const isNDecimalPlaces = (value, n) => {
  const decimalRegex = new RegExp(`^\\d*(\\.\\d{0,${n}})?$`);
  return decimalRegex.test(value);
};

export const isEmail = (value) => emailRegex.test(value);

export const isShippoEmail = (email: string) =>
  email.endsWith('@shippo.com') || email.endsWith('@goshippo.com');

const FRAUD_EMAIL_DOMAINS = [
  '@outlook.com',
  '@hotmail.com',
  '@yahoo.com',
  '@aol.com',
  '@aceomail.com',
  '@atorymail.com',
  '@bolivianomail.com',
  '@gmailmsn.us',
  '@mailtb.net',
  '@gmx.com',
  '@mailsecondary.com',
  '@chromomail.com',
  '@icloud.com',
  '@outlook.fr',
  '@rambler.ru',
];

export const isPotentialFraudEmail = (email: string) =>
  FRAUD_EMAIL_DOMAINS.some((domain) => email.toLowerCase().endsWith(domain));

const isPrivateIP = (hostname: string): boolean => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

  // Check if hostname is an IP address
  const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = hostname.match(ipv4Regex);

  if (!match) return false;

  const [, octet1, octet2, octet3, octet4] = match;
  const parts = [octet1, octet2, octet3, octet4].map(Number);

  // Validate octets are in valid range
  if (parts.some((part) => part < 0 || part > 255)) return true; // Invalid IP, treat as private for safety

  const [first, second] = parts;

  // Check private IP ranges
  // 10.0.0.0 - 10.255.255.255
  if (first === 10) return true;

  // 172.16.0.0 - 172.31.255.255
  if (first === 172 && second >= 16 && second <= 31) return true;

  // 192.168.0.0 - 192.168.255.255
  if (first === 192 && second === 168) return true;

  // 169.254.0.0 - 169.254.255.255 (link-local)
  if (first === 169 && second === 254) return true;

  // 127.0.0.0 - 127.255.255.255 (loopback)
  if (first === 127) return true;

  return false;
};

export const isValidURL = (value: string | undefined): boolean => {
  if (!value) return false;

  let url: URL;

  try {
    url = new URL(value);
  } catch {
    // This prevents malformed URLs like "ht!tp://example.com" from passing
    if (value.includes('://')) return false;
    try {
      url = new URL(`https://${value}`);
    } catch {
      return false;
    }
  }

  if (!['ftp:', 'http:', 'https:'].includes(url.protocol)) return false;

  if (isPrivateIP(url.hostname)) return false;

  return true;
};

const countryIso2ZipRegexMap = new Map([
  [
    'CA',
    /^([ABCEGHJKLMNPRSTVXYabceghjklmnprstvxy]\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]) {0,1}(\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\d)$/,
  ],
  ['MX', /^(\d{5})$/],
  ['US', /^\d{5}([-]?\d{4})?$/],
]);

export const isValidZipForCountry = (countryIso2, zip) => {
  if (countryIso2ZipRegexMap.has(countryIso2)) {
    const countryZipRegex = countryIso2ZipRegexMap.get(countryIso2)!;
    return countryZipRegex.test(zip);
  }
  return true;
};

export const isValidPhoneForCountry = (
  phone: null | string | undefined,
  country: Country | null | string | undefined,
): boolean => {
  if (!country || !phone) return true;

  let countryCode: string;
  if (typeof country === 'string') countryCode = country;
  else countryCode = (country as Country)?.iso2;

  const sanitized = sanitizeText(phone);
  /**
   * Fixes a legacy bug in the BE importing process where single-digit country calling codes
   * are improperly cleaned by the backend, resulting in an invalid international number. The FE
   * would then correctly mark those numbers as invalid. This issue affects US, CA, Caribbean
   * countries, Russia, and Kazakhstan.
   */
  const internalPhone = sanitized.startsWith('00') ? '+' + sanitized.slice(2) : sanitized;

  // We use `isPossiblePhoneNumber` so someone without a phone number can enter a 555 number
  // This is necessary in cases where someone doesn't have a phone (it happens) or they are unwilling to give their number
  // Someone choosing to do this will assume the risk of an undeliverable package.
  const isValid = isPossiblePhoneNumber(internalPhone, countryCode as CountryCode);

  if (!isValid) fullStoryTracking(FullStoryEvent.INVALID_ADDRESS_PHONE_NUMBER_SHOWN, {});

  return isValid;
};
